
import React, { useState, useEffect } from 'react';
import { ArrowRight, Award, Users, Calendar, Sword, Play, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import LazyYouTube from '@/components/LazyYouTube';
import { useTranslation } from 'react-i18next';

const Home = () => {
  const [hoveredWeapon, setHoveredWeapon] = useState<string | null>(null);
  const [showVideo, setShowVideo] = useState<string | null>(null);
  const [showNonCriticalContent, setShowNonCriticalContent] = useState(false);
  const { t } = useTranslation();

  const weaponVideos = {
    florett: 'g-P_q5gdvRM',
    degen: '88mBQ2u2Wb0', 
    saebel: '4ipiJFlU_Dg'
  };

  // Defer non-critical content to improve TBT
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowNonCriticalContent(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const getVideoId = (url: string) => {
    const match = url.match(/youtu\.be\/([^?]+)/);
    return match ? match[1] : '';
  };

  return (
    <div className="relative">
      {/* Hero Section with fencing image and misty overlay */}
      <section className="hero-section">
        {/* LCP-optimized hero image */}
        <img 
          src="/lovable-uploads/08f66298-356b-4d6b-91dd-90d5acd9fb03.png"
          alt=""
          className="hero-bg"
          loading="eager"
          width="1920"
          height="1080"
          decoding="sync"
        />
        
        {/* Optimized overlay gradients */}
        <div className="hero-overlay"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950/50 via-transparent to-slate-900/30"></div>

        <div className="hero-content">
          <h1 className="text-5xl md:text-7xl font-bold mb-6 bg-gradient-to-r from-amber-400 via-amber-300 to-yellow-400 bg-clip-text text-transparent">
            {t('home.hero.title')}
          </h1>
          
          <p className="text-xl md:text-2xl text-slate-200 mb-8 leading-relaxed drop-shadow-lg">
            {t('home.hero.description')}
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/training"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all duration-300 transform hover:scale-105 shadow-lg"
            >
              {t('home.hero.joinButton')}
              <ArrowRight className="ml-2" size={20} />
            </Link>
            
            <Link
              to="/about"
              className="inline-flex items-center px-8 py-4 border-2 border-amber-400 text-amber-400 font-semibold rounded-lg hover:bg-amber-400 hover:text-slate-900 transition-all duration-300 backdrop-blur-sm bg-slate-900/30"
              aria-label={t('home.hero.learnMoreAriaLabel', { defaultValue: 'Mehr über den ASC Fechten Wien erfahren' })}
            >
              {t('home.hero.learnMoreButton')}
            </Link>
          </div>
        </div>
      </section>

      {/* Regeln des Fechtens Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900/80 via-slate-800/60 to-slate-900/80">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-amber-400 mb-4">{t('home.fencingRules.title')}</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              {t('home.fencingRules.subtitle')}
            </p>
          </div>

          <div className="space-y-12">
            {/* Video */}
            <div className="max-w-4xl mx-auto">
              <LazyYouTube
                videoId="x7zxpDW8nb0"
                title={t('home.fencingRules.videoTitle')}
              />
            </div>

            {/* Regeln */}
            <div className="max-w-4xl mx-auto">
              <div className="bg-slate-900/50 rounded-2xl p-8 border border-amber-500/20">
                <h3 className="text-2xl font-bold text-amber-400 mb-6 text-center">{t('home.fencingRules.basicRulesTitle')}</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-lg font-semibold text-slate-200 mb-2">🎯 {t('home.fencingRules.targetZones.title')}</h4>
                    <p className="text-slate-300 text-sm">
                      {t('home.fencingRules.targetZones.description')}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-slate-200 mb-2">⚡ {t('home.fencingRules.rightOfWay.title')}</h4>
                    <p className="text-slate-300 text-sm">
                      {t('home.fencingRules.rightOfWay.description')}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-slate-200 mb-2">🏆 {t('home.fencingRules.scoring.title')}</h4>
                    <p className="text-slate-300 text-sm">
                      {t('home.fencingRules.scoring.description')}
                    </p>
                  </div>

                  <div>
                    <h4 className="text-lg font-semibold text-slate-200 mb-2">🛡️ {t('home.fencingRules.safety.title')}</h4>
                    <p className="text-slate-300 text-sm">
                      {t('home.fencingRules.safety.description')}
                    </p>
                  </div>

                  <div className="md:col-span-2">
                    <h4 className="text-lg font-semibold text-slate-200 mb-2">⚔️ {t('home.fencingRules.fairPlay.title')}</h4>
                    <p className="text-slate-300 text-sm">
                      {t('home.fencingRules.fairPlay.description')}
                    </p>
                  </div>
                </div>

                <div className="mt-8 p-4 bg-amber-500/10 rounded-lg border border-amber-500/20">
                  <p className="text-amber-400 font-semibold text-sm text-center">
                    💡 {t('home.fencingRules.tip')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Die drei Waffen Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900/60 via-slate-800/40 to-slate-900/60">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-amber-400 mb-4">{t('home.weapons.title')}</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              {t('home.weapons.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Florett */}
            <div className="bg-slate-900/50 rounded-2xl overflow-hidden border border-amber-500/20 group hover:border-amber-500/40 transition-all duration-300">
              <div 
                className="h-64 overflow-hidden group-hover:scale-105 transition-transform duration-300 p-4 relative cursor-pointer flex items-center justify-center"
                style={{background: 'radial-gradient(circle, transparent 30%, rgba(251, 191, 36, 0.2) 70%, rgba(245, 158, 11, 0.3) 100%)'}}
                onMouseEnter={() => setHoveredWeapon('florett')}
                onMouseLeave={() => setHoveredWeapon(null)}
                onClick={() => setShowVideo('florett')}
              >
                <picture>
                  <img 
                    src="/lovable-uploads/44ff05f7-8e8d-4c3e-aa2b-6d2cc9b8f29b.png" 
                    alt="Florett Waffe für das Fechttraining" 
                    className="max-w-full max-h-full object-contain rounded-lg"
                    width="381"
                    height="157"
                    loading="lazy"
                    sizes="(max-width: 768px) 300px, (max-width: 1024px) 350px, 381px"
                    style={{ objectFit: 'contain' }}
                  />
                </picture>
                {/* Always visible play button in top right */}
                <div className="absolute top-2 right-2 bg-amber-500 rounded-full p-2 hover:bg-amber-400 transition-colors shadow-lg">
                  <Play className="text-slate-900" size={16} fill="currentColor" />
                </div>
                {hoveredWeapon === 'florett' && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                    <div className="bg-amber-500 rounded-full p-4 hover:bg-amber-400 transition-colors">
                      <Play className="text-slate-900" size={24} fill="currentColor" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-amber-400 mb-4">{t('home.weapons.foil.name')}</h3>
                </div>
                <div className="space-y-4 text-slate-300">
                  <p className="leading-relaxed">
                    {t('home.weapons.foil.description')}
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li>• {t('home.weapons.foil.weight')}</li>
                    <li>• {t('home.weapons.foil.blade')}</li>
                    <li>• {t('home.weapons.foil.target')}</li>
                    <li>• {t('home.weapons.foil.rightOfWay')}</li>
                  </ul>
                  <p className="text-amber-400 font-semibold">
                    {t('home.weapons.foil.motto')}
                  </p>
                </div>
              </div>
            </div>

            {/* Degen */}
            <div className="bg-slate-900/50 rounded-2xl overflow-hidden border border-blue-500/20 group hover:border-blue-500/40 transition-all duration-300">
              <div 
                className="h-64 overflow-hidden group-hover:scale-105 transition-transform duration-300 p-4 relative cursor-pointer flex items-center justify-center"
                style={{background: 'radial-gradient(circle, transparent 30%, rgba(59, 130, 246, 0.2) 70%, rgba(37, 99, 235, 0.3) 100%)'}}
                onMouseEnter={() => setHoveredWeapon('degen')}
                onMouseLeave={() => setHoveredWeapon(null)}
                onClick={() => setShowVideo('degen')}
              >
                <picture>
                  <img 
                    src="/lovable-uploads/62dfff47-7630-4881-9fbf-54fb5933644a.png" 
                    alt="Degen Waffe für das Fechttraining" 
                    className="max-w-full max-h-full object-contain rounded-lg"
                    width="381"
                    height="157"
                    loading="lazy"
                    sizes="(max-width: 768px) 300px, (max-width: 1024px) 350px, 381px"
                    style={{ objectFit: 'contain' }}
                  />
                </picture>
                {/* Always visible play button in top right */}
                <div className="absolute top-2 right-2 bg-blue-500 rounded-full p-2 hover:bg-blue-400 transition-colors shadow-lg">
                  <Play className="text-slate-900" size={16} fill="currentColor" />
                </div>
                {hoveredWeapon === 'degen' && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                    <div className="bg-blue-500 rounded-full p-4 hover:bg-blue-400 transition-colors">
                      <Play className="text-slate-900" size={24} fill="currentColor" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-blue-400 mb-4">{t('home.weapons.epee.name')}</h3>
                </div>
                <div className="space-y-4 text-slate-300">
                  <p className="leading-relaxed">
                    {t('home.weapons.epee.description')}
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li>• {t('home.weapons.epee.weight')}</li>
                    <li>• {t('home.weapons.epee.blade')}</li>
                    <li>• {t('home.weapons.epee.target')}</li>
                    <li>• {t('home.weapons.epee.rightOfWay')}</li>
                  </ul>
                  <p className="text-blue-400 font-semibold">
                    {t('home.weapons.epee.motto')}
                  </p>
                </div>
              </div>
            </div>

            {/* Säbel */}
            <div className="bg-slate-900/50 rounded-2xl overflow-hidden border border-green-500/20 group hover:border-green-500/40 transition-all duration-300">
              <div 
                className="h-64 overflow-hidden group-hover:scale-105 transition-transform duration-300 p-4 relative cursor-pointer flex items-center justify-center"
                style={{background: 'radial-gradient(circle, transparent 30%, rgba(34, 197, 94, 0.2) 70%, rgba(22, 163, 74, 0.3) 100%)'}}
                onMouseEnter={() => setHoveredWeapon('saebel')}
                onMouseLeave={() => setHoveredWeapon(null)}
                onClick={() => setShowVideo('saebel')}
              >
                <picture>
                  <img 
                    src="/lovable-uploads/bd125961-9730-40e5-aa9b-8e4f2d3db84c.png" 
                    alt="Säbel Waffe für das Fechttraining" 
                    className="max-w-full max-h-full object-contain rounded-lg"
                    width="381"
                    height="157"
                    loading="lazy"
                    sizes="(max-width: 768px) 300px, (max-width: 1024px) 350px, 381px"
                    style={{ objectFit: 'contain' }}
                  />
                </picture>
                {/* Always visible play button in top right */}
                <div className="absolute top-2 right-2 bg-green-500 rounded-full p-2 hover:bg-green-400 transition-colors shadow-lg">
                  <Play className="text-slate-900" size={16} fill="currentColor" />
                </div>
                {hoveredWeapon === 'saebel' && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center rounded-lg">
                    <div className="bg-green-500 rounded-full p-4 hover:bg-green-400 transition-colors">
                      <Play className="text-slate-900" size={24} fill="currentColor" />
                    </div>
                  </div>
                )}
              </div>
              <div className="p-8">
                <div className="text-center mb-6">
                  <h3 className="text-2xl font-bold text-green-400 mb-4">{t('home.weapons.sabre.name')}</h3>
                </div>
                <div className="space-y-4 text-slate-300">
                  <p className="leading-relaxed">
                    {t('home.weapons.sabre.description')}
                  </p>
                  <ul className="space-y-2 text-sm">
                    <li>• {t('home.weapons.sabre.weight')}</li>
                    <li>• {t('home.weapons.sabre.blade')}</li>
                    <li>• {t('home.weapons.sabre.target')}</li>
                    <li>• {t('home.weapons.sabre.rightOfWay')}</li>
                  </ul>
                  <p className="text-green-400 font-semibold">
                    {t('home.weapons.sabre.motto')}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <p className="text-slate-300 mb-6">
              {t('home.weapons.tryAllWeapons')}
            </p>
            <Link
              to="/training"
              className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all duration-300 transform hover:scale-105"
              onClick={() => window.scrollTo(0, 0)}
            >
              {t('home.weapons.toTrialTraining')}
              <ArrowRight className="ml-2" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-900/50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-amber-400 mb-4">{t('home.about.title')}</h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              {t('home.about.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-amber-500/20 to-amber-600/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Sword className="text-amber-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-amber-400 mb-3">{t('home.about.features.training.title')}</h3>
              <p className="text-slate-300">{t('home.about.features.training.description')}</p>
            </div>

            <div className="text-center group">
              <div className="bg-gradient-to-br from-blue-500/20 to-blue-600/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Users className="text-blue-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-blue-400 mb-3">{t('home.about.features.community.title')}</h3>
              <p className="text-slate-300">{t('home.about.features.community.description')}</p>
            </div>

            <div className="text-center group">
              <div className="bg-gradient-to-br from-green-500/20 to-green-600/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Award className="text-green-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-green-400 mb-3">{t('home.about.features.tournaments.title')}</h3>
              <p className="text-slate-300">{t('home.about.features.tournaments.description')}</p>
            </div>

            <div className="text-center group">
              <div className="bg-gradient-to-br from-purple-500/20 to-purple-600/20 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                <Calendar className="text-purple-400" size={32} />
              </div>
              <h3 className="text-xl font-semibold text-purple-400 mb-3">{t('home.about.features.schedule.title')}</h3>
              <p className="text-slate-300">{t('home.about.features.schedule.description')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-amber-600/10 to-blue-600/10">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-amber-400 mb-6">{t('home.cta.title')}</h2>
          <p className="text-xl text-slate-300 mb-8 max-w-2xl mx-auto">
            {t('home.cta.description')}
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-500 to-amber-600 text-slate-900 font-semibold rounded-lg hover:from-amber-400 hover:to-amber-500 transition-all duration-300 transform hover:scale-105"
            onClick={() => window.scrollTo(0, 0)}
          >
            {t('home.cta.contactButton')}
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* Video Modal */}
      {showVideo && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
          <div className="bg-slate-900 rounded-lg overflow-hidden max-w-4xl w-full relative">
            <button
              onClick={() => setShowVideo(null)}
              className="absolute top-4 right-4 z-10 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 transition-colors"
              aria-label={t('home.cta.closeVideo')}
            >
              <X size={20} />
            </button>
            <div className="aspect-video">
              <LazyYouTube
                videoId={weaponVideos[showVideo as keyof typeof weaponVideos]}
                title="Fencing Video"
                autoplay={true}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
